import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TestTube2,
  Play,
  CheckCircle2,
  Plus,
  ArrowRight,
  Activity,
  TrendingUp,
  TrendingDown,
  Brain,
  Search,
  Users,
  Sparkles,
  ChevronRight,
  FileQuestion,
  Stethoscope,
  Target,
} from 'lucide-react';
import { clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../../lib/api';
import { ValidationDashboardSkeleton, ErrorState, Skeleton } from '../../components/ui';

interface DashboardStats {
  testCases: {
    total: number;
    active: number;
    draft: number;
  };
  testRuns: {
    total: number;
    completed: number;
    byStatus: Array<{ status: string; count: number }>;
  };
  executions: {
    total: number;
    completed: number;
    successRate: string | number;
  };
  evaluations: {
    total: number;
  };
  casesByCategory: Array<{ category: string; count: number }>;
  recentTestRuns: Array<{
    id: string;
    name: string;
    status: string;
    createdAt: string;
    completedAt: string | null;
    testCase: { name: string; code: string };
  }>;
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: { value: number; label: string; positive?: boolean };
  color?: 'blue' | 'green' | 'amber' | 'red';
  isLoading?: boolean;
  href?: string;
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color = 'blue',
  isLoading = false,
  href,
}: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton width={80} height={14} />
            <Skeleton width={60} height={28} />
            <Skeleton width={100} height={12} />
          </div>
          <Skeleton width={48} height={48} variant="rectangular" className="rounded-lg" />
        </div>
      </div>
    );
  }

  const content = (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        {trend && (
          <div
            className={clsx(
              'mt-2 flex items-center gap-1 text-xs',
              trend.positive !== false ? 'text-green-600' : 'text-red-600'
            )}
          >
            {trend.positive !== false ? (
              <TrendingUp className="h-3 w-3" />
            ) : (
              <TrendingDown className="h-3 w-3" />
            )}
            <span className="font-medium">
              {trend.positive !== false ? '+' : ''}
              {trend.value}%
            </span>
            <span className="text-gray-500">{trend.label}</span>
          </div>
        )}
      </div>
      <div className={clsx('rounded-lg p-3', colorClasses[color])}>
        <Icon className="h-6 w-6" />
      </div>
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="block bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md hover:border-amber-300 transition-all"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      {content}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { color: string; label: string; dot?: string }> = {
    DRAFT: { color: 'bg-gray-100 text-gray-700', label: 'Draft', dot: 'bg-gray-400' },
    PENDING: { color: 'bg-blue-100 text-blue-700', label: 'Pending', dot: 'bg-blue-400' },
    RUNNING: { color: 'bg-amber-100 text-amber-700', label: 'Running', dot: 'bg-amber-400 animate-pulse' },
    PAUSED: { color: 'bg-yellow-100 text-yellow-700', label: 'Paused', dot: 'bg-yellow-400' },
    COMPLETED: { color: 'bg-green-100 text-green-700', label: 'Completed', dot: 'bg-green-400' },
    FAILED: { color: 'bg-red-100 text-red-700', label: 'Failed', dot: 'bg-red-400' },
    CANCELLED: { color: 'bg-gray-100 text-gray-700', label: 'Cancelled', dot: 'bg-gray-400' },
  };

  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', label: status, dot: 'bg-gray-400' };

  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', config.color)}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', config.dot)} />
      {config.label}
    </span>
  );
}

function ProductionModelFlow() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="font-semibold text-gray-900">BetterMatch Production Model Setup</h2>
        <p className="text-sm text-gray-500 mt-0.5">Current AI models used in the healthcare provider matching workflow</p>
      </div>

      <div className="p-5">
        {/* Two Phase Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Phase 2+3: Case Analysis */}
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-100 text-violet-700 text-xs font-bold">1</div>
              <h3 className="font-semibold text-gray-900">Phase 2+3: Case Analysis</h3>
            </div>

            {/* Model Badge */}
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2">
              <Brain className="h-4 w-4 text-violet-600" />
              <div>
                <span className="text-sm font-semibold text-violet-900">Claude Opus 4.6</span>
                <span className="text-xs text-violet-600 ml-2">Adaptive Thinking</span>
              </div>
            </div>

            {/* Workflow Steps */}
            <div className="space-y-3 pl-3 border-l-2 border-violet-200">
              <div className="relative flex items-start gap-3 -ml-[9px]">
                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 ring-4 ring-white">
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                </div>
                <div className="flex-1 min-w-0 pt-px">
                  <div className="flex items-center gap-2">
                    <FileQuestion className="h-4 w-4 text-violet-600" />
                    <span className="text-sm font-medium text-gray-900">Patient Questionnaire</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Generate 15-20 tailored medical questions</p>
                </div>
              </div>

              <div className="relative flex items-start gap-3 -ml-[9px]">
                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 ring-4 ring-white">
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                </div>
                <div className="flex-1 min-w-0 pt-px">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-violet-600" />
                    <span className="text-sm font-medium text-gray-900">Indication Analysis</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Analyze medical appropriateness & patient expectations</p>
                </div>
              </div>

              <div className="relative flex items-start gap-3 -ml-[9px]">
                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 ring-4 ring-white">
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                </div>
                <div className="flex-1 min-w-0 pt-px">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-violet-600" />
                    <span className="text-sm font-medium text-gray-900">Matching Criteria</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Create prioritized provider matching criteria</p>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 4: Care Provider Matching */}
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">2</div>
              <h3 className="font-semibold text-gray-900">Phase 4: Care Provider Matching</h3>
            </div>

            {/* Model Badge */}
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2">
              <Search className="h-4 w-4 text-green-600" />
              <div>
                <span className="text-sm font-semibold text-green-900">Gemini 3 Pro</span>
                <span className="text-xs text-green-600 ml-2">Google Search</span>
              </div>
            </div>

            {/* Search Step */}
            <div className="space-y-3 pl-3 border-l-2 border-emerald-200 mb-4">
              <div className="relative flex items-start gap-3 -ml-[9px]">
                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 ring-4 ring-white">
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                </div>
                <div className="flex-1 min-w-0 pt-px">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-gray-900">Provider Discovery</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Match & rank Swiss healthcare providers</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Grand Rounds Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <h3 className="font-semibold text-gray-900">Grand Rounds: Multi-Persona Consensus</h3>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Advanced Mode</span>
          </div>

          <p className="text-sm text-gray-500 mb-4">Three expert personas with distinct clinical perspectives debate each case. Models configurable per expert in test run setup.</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Dr. Precision */}
            <div className="relative rounded-lg border border-gray-200 bg-gradient-to-br from-red-50 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                  <Brain className="h-5 w-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-gray-900 text-sm">Dr. Precision</span>
                  </div>
                  <span className="text-xs font-medium text-red-600">Conservative Specialist</span>
                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">Focused on safety, risk assessment & documentation</p>
                  <div className="mt-2 inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5">
                    <span className="text-[10px] text-gray-500">Default: Opus 4.6</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dr. Evidence */}
            <div className="relative rounded-lg border border-gray-200 bg-gradient-to-br from-emerald-50 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                  <Brain className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-gray-900 text-sm">Dr. Evidence</span>
                  </div>
                  <span className="text-xs font-medium text-emerald-600">Evidence-Based Practitioner</span>
                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">Emphasizes clinical guidelines & outcomes research</p>
                  <div className="mt-2 inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5">
                    <span className="text-[10px] text-gray-500">Default: Opus 4.6</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dr. Advocate */}
            <div className="relative rounded-lg border border-gray-200 bg-gradient-to-br from-blue-50 to-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <Brain className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-gray-900 text-sm">Dr. Advocate</span>
                  </div>
                  <span className="text-xs font-medium text-blue-600">Patient-Centered Advocate</span>
                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">Prioritizes accessibility & quality of life</p>
                  <div className="mt-2 inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5">
                    <span className="text-[10px] text-gray-500">Default: Opus 4.6</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Flow Arrow */}
          <div className="flex items-center justify-center gap-2 mt-4 text-gray-400">
            <div className="h-px flex-1 bg-gray-200" />
            <ChevronRight className="h-4 w-4" />
            <span className="text-xs font-medium">Moderator Synthesis</span>
            <span className="text-[10px] text-gray-400">(Opus 4.6 + Adaptive thinking)</span>
            <ChevronRight className="h-4 w-4" />
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <div className="mt-3 flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2">
              <Sparkles className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-900">Final Consensus</span>
              <span className="text-xs text-indigo-600">Unified Recommendation</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryBreakdown({ categories, total }: { categories: Array<{ category: string; count: number }>; total: number }) {
  return (
    <div className="space-y-4">
      {categories.map((cat) => {
        const percentage = total > 0 ? (cat.count / total) * 100 : 0;
        return (
          <div key={cat.category} className="group">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 capitalize">
                {cat.category.replace(/_/g, ' ').toLowerCase()}
              </span>
              <span className="text-sm font-bold text-gray-900">{cat.count}</span>
            </div>
            <div className="relative h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="absolute h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, percentage)}%` }}
              />
            </div>
            <div className="mt-0.5 text-xs text-gray-400 text-right">{percentage.toFixed(1)}%</div>
          </div>
        );
      })}
    </div>
  );
}

export function ValidationDashboard() {
  const { data: stats, isLoading, error, refetch } = useQuery<{ data: DashboardStats }>({
    queryKey: ['validation', 'analytics', 'summary'],
    queryFn: () => api.get('/api/validation/analytics/summary'),
  });

  if (isLoading) {
    return <ValidationDashboardSkeleton />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load dashboard"
        message="Unable to load dashboard statistics. Please check your connection and try again."
        helpText="If this problem persists, please contact support."
        onRetry={() => refetch()}
        variant="card"
      />
    );
  }

  const data = stats?.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Model Validation Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage LLM model validation for healthcare provider matching
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/validation/test-cases/new"
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
          >
            <Plus className="h-4 w-4" />
            New Test Case
          </Link>
          <Link
            to="/validation/test-runs/new"
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 transition-colors"
          >
            <Play className="h-4 w-4" />
            Start Test Run
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Test Cases"
          value={data?.testCases.total || 0}
          subtitle={`${data?.testCases.active || 0} active, ${data?.testCases.draft || 0} draft`}
          icon={TestTube2}
          color="blue"
          href="/validation/test-cases"
        />
        <StatCard
          title="Test Runs"
          value={data?.testRuns.completed || 0}
          subtitle={`of ${data?.testRuns.total || 0} total`}
          icon={Play}
          color="amber"
          href="/validation/test-runs"
        />
        <StatCard
          title="Executions"
          value={data?.executions.completed || 0}
          subtitle={`${data?.executions.successRate || 0}% success rate`}
          icon={Activity}
          color="green"
          href="/validation/test-runs"
        />
        <StatCard
          title="Evaluations"
          value={data?.evaluations.total || 0}
          subtitle="completed reviews"
          icon={CheckCircle2}
          color="blue"
          href="/validation/evaluations"
        />
      </div>

      {/* Production Model Flow */}
      <ProductionModelFlow />

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Test Runs */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <h2 className="font-semibold text-gray-900">Recent Test Runs</h2>
            <Link
              to="/validation/test-runs"
              className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {data?.recentTestRuns && data.recentTestRuns.length > 0 ? (
              data.recentTestRuns.map((run) => (
                <Link
                  key={run.id}
                  to={`/validation/test-runs/${run.id}`}
                  className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors group"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 group-hover:text-amber-700 transition-colors truncate">
                      {run.name}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="font-mono text-xs">{run.testCase.code}</span>
                      <span>·</span>
                      <span className="truncate">{run.testCase.name}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDistanceToNow(new Date(run.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  <StatusBadge status={run.status} />
                </Link>
              ))
            ) : (
              <div className="px-5 py-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <Play className="h-6 w-6 text-gray-400" />
                </div>
                <p className="mt-3 text-sm font-medium text-gray-900">No test runs yet</p>
                <p className="mt-1 text-sm text-gray-500">
                  Start your first test run to validate model performance
                </p>
                <Link
                  to="/validation/test-runs/new"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700"
                >
                  Start your first test run
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Test Cases by Category */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <h2 className="font-semibold text-gray-900">Test Cases by Category</h2>
            <Link
              to="/validation/test-cases"
              className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="p-5">
            {data?.casesByCategory && data.casesByCategory.length > 0 ? (
              <CategoryBreakdown
                categories={data.casesByCategory}
                total={data.testCases.total || 1}
              />
            ) : (
              <div className="py-8 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <TestTube2 className="h-6 w-6 text-gray-400" />
                </div>
                <p className="mt-3 text-sm font-medium text-gray-900">No test cases yet</p>
                <p className="mt-1 text-sm text-gray-500">
                  Create test cases to start validating models
                </p>
                <Link
                  to="/validation/test-cases/new"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700"
                >
                  Create your first test case
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Link
          to="/validation/test-cases/new"
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:border-amber-300 hover:shadow-md transition-all group"
        >
          <div className="rounded-lg bg-blue-100 p-3 group-hover:bg-blue-200 transition-colors">
            <TestTube2 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">Create Test Case</p>
            <p className="text-sm text-gray-500">Add a new validation scenario</p>
          </div>
        </Link>
        <Link
          to="/validation/test-runs/new"
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:border-amber-300 hover:shadow-md transition-all group"
        >
          <div className="rounded-lg bg-amber-100 p-3 group-hover:bg-amber-200 transition-colors">
            <Play className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">Run Model Validation</p>
            <p className="text-sm text-gray-500">Execute models on test cases</p>
          </div>
        </Link>
        <Link
          to="/validation/analytics"
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:border-amber-300 hover:shadow-md transition-all group"
        >
          <div className="rounded-lg bg-green-100 p-3 group-hover:bg-green-200 transition-colors">
            <Activity className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">View Analytics</p>
            <p className="text-sm text-gray-500">Compare model performance</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
